import type { Dispatch, SetStateAction } from 'react';
import { useSearchParams } from 'react-router-dom';

/** How a value is read from the address and written back into it. */
export interface SearchParamCodec<T> {
  read: (params: URLSearchParams) => T;
  /** The address with `value` in it, leaving every other entry alone. */
  write: (params: URLSearchParams, value: T) => URLSearchParams;
}

/**
 * Like `useState`, but the value lives in the address, so it survives a
 * refresh and Back. Writes replace the history entry rather than adding one.
 */
export function useSearchParamState<T>(
  codec: SearchParamCodec<T>,
): [T, Dispatch<SetStateAction<T>>] {
  const [params, setParams] = useSearchParams();

  const setValue: Dispatch<SetStateAction<T>> = (action) =>
    setParams(
      (prev) => {
        const next = action instanceof Function ? action(codec.read(prev)) : action;
        return codec.write(prev, next);
      },
      { replace: true },
    );

  return [codec.read(params), setValue];
}
